import { useEffect, useRef, useState, useCallback } from "react";
import api, { apiErr, getToken } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Sparkles, X, Send, Plus, Trash2, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { translations } from "@/i18n";

function renderMd(text, onInsert) {
  const parts = text.split(/```(\w*)\n?([\s\S]*?)```/g);
  const out = [];
  for (let i = 0; i < parts.length; i += 3) {
    if (parts[i]) out.push(<p key={`t${i}`} className="whitespace-pre-wrap leading-relaxed">{parts[i].replace(/\*\*(.+?)\*\*/g, "$1")}</p>);
    if (parts[i + 2] !== undefined) {
      const code = parts[i + 2];
      out.push(
        <div key={`c${i}`} className="relative group my-2">
          <pre className="bg-slate-900 text-slate-100 text-[11px] rounded-lg p-3 overflow-x-auto font-mono">{code}</pre>
          <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100">
            {onInsert && <button onClick={() => onInsert(code)} className="text-[10px] font-semibold bg-orange-600 hover:bg-orange-500 text-white rounded px-2 py-0.5" data-testid="copilot-insert-cell">→ Notebook</button>}
            <button onClick={() => { navigator.clipboard.writeText(code); toast.success((translations[localStorage.getItem("bvd_lang") || "fr"] || translations.fr).copied); }} className="text-slate-300 hover:text-white" data-testid="copilot-copy-code"><Copy className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      );
    }
  }
  return out;
}

function InsertCellModal({ code, onClose }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [target, setTarget] = useState("__new__");
  const [name, setName] = useState("");
  const [md, setMd] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/notebooks/library").then((r) => setItems(r.data.filter((n) => !n.kit && n.can_edit))).catch(() => {}); }, []);
  const go = async () => {
    const file = target === "__new__" ? `${name.trim().replace(/[^A-Za-z0-9_\-]+/g, "_")}.ipynb` : target;
    if (target === "__new__" && !name.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/notebooks/library/${encodeURIComponent(file)}/cells`, { code, markdown: md, create: target === "__new__" }); toast.success(`${t("cp_inserted")} ${data.file} (${data.cells} ${t("cp_cells")})`); onClose(true); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-center justify-center p-4" data-testid="copilot-insert-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <h3 className="font-head font-semibold text-slate-900 mb-1">{t("cp_insert_title")}</h3>
        <p className="text-xs text-slate-500 mb-4">{t("cp_insert_desc")}</p>
        <label className="text-xs font-mono uppercase tracking-wider text-slate-500">{t("cp_insert_target")}</label>
        <select data-testid="copilot-insert-target" value={target} onChange={(e) => setTarget(e.target.value)} className="w-full mt-1 mb-3 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm">
          <option value="__new__">{t("cp_insert_new")}</option>
          {items.map((n) => <option key={n.file} value={n.file}>{n.file}</option>)}
        </select>
        {target === "__new__" && <input data-testid="copilot-insert-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("cp_insert_name_ph")} className="w-full mb-3 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm" />}
        <input data-testid="copilot-insert-md" value={md} onChange={(e) => setMd(e.target.value)} placeholder={t("cp_insert_md_ph")} className="w-full mb-3 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm" />
        <pre className="bg-slate-900 text-slate-100 text-[11px] rounded-lg p-3 max-h-40 overflow-auto font-mono mb-4">{code}</pre>
        <div className="flex justify-end gap-2">
          <button onClick={() => onClose(false)} className="text-sm font-semibold text-slate-600 px-4 py-2 rounded-xl hover:bg-slate-50">{t("cancel")}</button>
          <button data-testid="copilot-insert-confirm" onClick={go} disabled={busy || (target === "__new__" && !name.trim())} className="text-sm font-semibold text-white bg-orange-600 hover:bg-orange-500 disabled:opacity-50 px-4 py-2 rounded-xl">{t("cp_insert_confirm")}</button>
        </div>
      </div>
    </div>
  );
}

const READY = { HIGH: "bg-emerald-50 text-emerald-700 border-emerald-200", MODERATE: "bg-amber-50 text-amber-700 border-amber-200", LOW: "bg-red-50 text-red-700 border-red-200" };

export function CopilotChat({ mode: initialMode = "global", compact = false, onClose }) {
  const { t, lang } = useI18n();
  const [mode, setMode] = useState(initialMode === "global" ? "epi_interpretation" : initialMode);
  const [si, setSi] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [showEv, setShowEv] = useState(false);
  useEffect(() => { if (initialMode !== "notebooks") api.get("/copilot/modes").then((r) => setSi(r.data)).catch(() => {}); }, [initialMode]);
  const draftAction = async (content) => {
    try {
      const { data } = await api.post("/copilot/actions/draft", { problem_statement: content.slice(0, 500), evidence: (evidence?.indicators || []).slice(0, 5).map((i) => `${i.code}=${i.valeur}${i.unite || ""}`).join("; "), recommendation: content.slice(0, 600) });
      toast.success(`${t("cp_action_created")} ${data.action_id}`, { action: { label: t("cp_open_decisions"), onClick: () => { window.location.href = "/app/analyses/decisions"; } } });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const [insert, setInsert] = useState(null);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(localStorage.getItem("bvd_copilot_model") || "gpt-5.4");
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const loadSessions = useCallback(() => api.get(`/copilot/sessions?mode=${mode}`).then((r) => setSessions(r.data)).catch(() => {}), [mode]);
  useEffect(() => { api.get("/copilot/models").then((r) => setModels(r.data)).catch(() => {}); loadSessions(); }, [loadSessions]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const openSession = async (s) => { setSid(s.id); const { data } = await api.get(`/copilot/sessions/${s.id}/messages`); setMsgs(data); };
  const newSession = () => { setSid(null); setMsgs([]); };
  const delSession = async (s) => { await api.delete(`/copilot/sessions/${s.id}`); if (sid === s.id) newSession(); loadSessions(); };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/copilot/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
        body: JSON.stringify({ message: text, session_id: sid, mode, model, lang }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || res.statusText); }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      while (true) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n"); buf = lines.pop();
        for (const l of lines) {
          if (!l.startsWith("data: ")) continue;
          const ev = JSON.parse(l.slice(6));
          if (ev.session_id && !sid) setSid(ev.session_id);
          if (ev.evidence) setEvidence(ev.evidence);
          if (ev.delta) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + ev.delta }; return c; });
        }
      }
      loadSessions();
    } catch (e) {
      toast.error(apiErr(e.message));
      setMsgs((m) => m.slice(0, -1));
    } finally { setBusy(false); }
  };

  const SUG = { ask_data: [t("cp_sq1"), t("cp_sq2")], epi_interpretation: [t("cp_sug_g1"), t("cp_sug_g2")], data_quality: [t("cp_sq3"), t("cp_sq4")], program_performance: [t("cp_sq5")],
    briefing: [t("cp_sq6")], dhis2_support: [t("cp_sq7")], kobo_support: [t("cp_sq8")], indicator_explainer: [t("cp_sq9")] };
  const suggestions = mode === "notebooks" ? [t("cp_sug_nb1"), t("cp_sug_nb2"), t("cp_sug_nb3")] : (SUG[mode] || [t("cp_sug_g1")]);

  return (
    <div className={`flex flex-col ${compact ? "h-[560px]" : "h-[640px]"} bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden`} data-testid={`copilot-${mode}`}>
      {insert && <InsertCellModal code={insert} onClose={() => setInsert(null)} />}
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 text-white">
        <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-300" /><span className="font-head font-semibold text-sm">{mode === "notebooks" ? t("cp_title_nb") : t("cp_title_global")}</span></div>
        <div className="flex items-center gap-2">
          <select data-testid="copilot-model" value={model} onChange={(e) => { setModel(e.target.value); localStorage.setItem("bvd_copilot_model", e.target.value); }} className="bg-slate-800 text-[11px] rounded-md px-2 py-1 border border-slate-700 outline-none">
            {models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <button data-testid="copilot-new" onClick={newSession} title={t("cp_new")} className="text-slate-300 hover:text-white"><Plus className="w-4 h-4" /></button>
          {onClose && <button data-testid="copilot-close" onClick={onClose} className="text-slate-300 hover:text-white"><X className="w-4 h-4" /></button>}
        </div>
      </div>
      {mode !== "notebooks" && si && (
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 space-y-1.5" data-testid="copilot-scope">
          <div className="flex flex-wrap items-center gap-1.5 text-[10.5px] text-slate-600">
            <span className="font-semibold text-slate-800">{si.scope.projets.join(" · ")}</span><span>·</span><span>{si.scope.semaine}</span><span>·</span>
            <span>{si.scope.zones === "toutes" ? t("home_all_zones") : si.scope.zones.join(", ")}</span>
            <span data-testid="copilot-readiness" title={si.readiness.reason} className={`ml-auto px-2 py-0.5 rounded border font-semibold ${READY[si.readiness.level]}`}>{t("cp_readiness")} {si.readiness.level}</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {si.modes.filter((m) => m.id !== "notebooks").map((m) => <button key={m.id} data-testid={`copilot-mode-${m.id}`} onClick={() => setMode(m.id)} className={`text-[10.5px] px-2 py-0.5 rounded border transition-colors ${mode === m.id ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{m.label}</button>)}
          </div>
        </div>
      )}
      <div className="flex flex-1 min-h-0">
        {!compact && (
          <div className="w-48 border-r border-slate-100 overflow-y-auto p-2 space-y-1 bg-slate-50/60" data-testid="copilot-sessions">
            {sessions.map((s) => (
              <div key={s.id} className={`group flex items-center justify-between text-[11px] rounded-md px-2 py-1.5 cursor-pointer ${sid === s.id ? "bg-white border border-slate-200" : "hover:bg-white"}`} onClick={() => openSession(s)}>
                <span className="truncate text-slate-700">{s.title}</span>
                <button onClick={(e) => { e.stopPropagation(); delSession(s); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
            {!sessions.length && <div className="text-[11px] text-slate-400 p-2">{t("cp_no_session")}</div>}
          </div>
        )}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm" data-testid="copilot-messages">
            {!msgs.length && (
              <div className="text-slate-500 space-y-2">
                <p className="text-xs">{mode === "notebooks" ? t("cp_intro_nb") : t("cp_intro_global")}</p>
                <div className="flex flex-wrap gap-2">{suggestions.map((s) => <button key={s} data-testid="copilot-suggestion" onClick={() => setInput(s)} className="text-[11px] border border-slate-200 rounded-full px-3 py-1 hover:bg-slate-50 text-slate-700">{s}</button>)}</div>
                <p className="text-[10px] text-slate-400">{t("cp_privacy")}</p>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 ${m.role === "user" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-900"}`} data-testid={`copilot-msg-${m.role}`}>
                  {m.role === "user" ? <p className="whitespace-pre-wrap">{m.content}</p> : (m.content ? renderMd(m.content, mode === "notebooks" ? setInsert : null) : <Loader2 className="w-4 h-4 animate-spin text-slate-400" />)}
                  {m.role === "assistant" && m.content && mode !== "notebooks" && i === msgs.length - 1 && !busy && (
                    <div className="flex gap-2 mt-2 pt-2 border-t border-slate-200/70">
                      <button data-testid="copilot-evidence-toggle" onClick={() => setShowEv((v) => !v)} className="text-[10.5px] font-medium text-slate-600 hover:text-slate-900">{t("cp_view_evidence")}</button>
                      <button data-testid="copilot-create-action" onClick={() => draftAction(m.content)} className="text-[10.5px] font-medium text-slate-600 hover:text-slate-900">{t("cp_create_action")}</button>
                    </div>
                  )}
                  {m.role === "assistant" && showEv && evidence && i === msgs.length - 1 && (
                    <div className="mt-2 rounded-md border border-slate-200 bg-white p-2 text-[10.5px] text-slate-700 space-y-1" data-testid="copilot-evidence">
                      <div><b>{t("cp_readiness")}</b> : {evidence.readiness.level} · {evidence.readiness.reason}</div>
                      <div><b>{t("home_zones")}</b> : {evidence.scope.zones === "toutes" ? t("home_all_zones") : evidence.scope.zones.join(", ")} · <b>{t("home_projects")}</b> : {evidence.scope.projets.join(", ")}</div>
                      {evidence.datasets.map((d) => <div key={d.dataset}>{d.dataset} : n={d.n}{d.quarantaine != null ? ` (${d.quarantaine} ${t("exp_review")})` : ""}</div>)}
                      <div><b>{t("nav_indicators")}</b> : {evidence.indicators.slice(0, 8).map((x) => `${x.code}=${x.valeur ?? "·"}${x.unite === "%" ? "%" : ""} [${x.validation}]`).join(" · ")}</div>
                      <div className="text-slate-400">{t("cp_modules")} : {evidence.modules_accessed.join(", ")}</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="border-t border-slate-100 p-3 flex gap-2">
            <textarea data-testid="copilot-input" rows={2} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={t("cp_placeholder")} className="flex-1 resize-none bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-slate-900" />
            <button data-testid="copilot-send" onClick={send} disabled={busy || !input.trim()} className="self-end bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-xl px-3 py-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CopilotFloating() {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  return (
    <>
      {open && <div className="fixed bottom-20 right-6 z-50 w-[420px] max-w-[calc(100vw-3rem)]"><CopilotChat mode="global" compact onClose={() => setOpen(false)} /></div>}
      <button data-testid="copilot-fab" onClick={() => setOpen(!open)} title={t("cp_title_global")}
        className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-3 pr-4 py-2.5 shadow-xl hover:bg-slate-800 transition-transform hover:-translate-y-0.5">
        <Sparkles className="w-4 h-4 text-amber-300" /><span className="text-sm font-semibold">{t("cp_fab")}</span>
      </button>
    </>
  );
}
